import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useToast } from '@vtex/admin-ui'
// eslint-disable-next-line boundaries/element-types
import { type FullProductData } from '@/features/product/hooks'
import { PRODUCT_LISTING_URL } from '@/hooks/useProducts'
import { fetcher } from '@/lib/fetcher'
import { mutate } from '@/lib/mutate'
import { removeIdFromField } from '@/lib/transform'

type FormatMessage = ReturnType<typeof useIntl>['formatMessage']

const copyProduct = async (productId: number, formatMessage: FormatMessage) => {
  const productToCopy = (await fetcher({
    url: `/api/proxy/catalog/pvt/admin/form/product/${productId}`,
  })) as FullProductData

  const validProductToCopy = {
    ...productToCopy,
    Specifications: productToCopy.Specifications?.filter(
      (spec) => spec.FieldTypeHasValue || !!spec.ProductFieldValueText
    ),
  }

  const productCopy = removeIdFromField(validProductToCopy, [
    'Id',
    'ProductId',
    'LinkId',
    'ArchiveId',
    'Eans',
    'RefId',
    'LastUpdateDate',
    'LastUpdateEditor',
    'ReleaseDate',
    'ParentSkuId',
    'ProductFieldValueId',
    'SkuFieldValueId',
  ])

  if (!productCopy?.Name) {
    throw new Error('Error fetching product to copy')
  }

  const copy = await fetcher({
    url: `/api/proxy/catalog/pvt/admin/form/product`,
    method: 'POST',
    body: JSON.stringify({
      ...productCopy,
      Name: formatMessage(
        {
          id: 'admin/admin.catalog.copy-product.name',
          defaultMessage: 'Copy of {productName}',
        },
        {
          productName: productCopy.Name,
        }
      ),
    }),
  })

  if (!copy) {
    throw new Error('Error copying product')
  }

  mutate(PRODUCT_LISTING_URL)
}

export const useCopyProduct = (productId: number) => {
  const showToast = useToast()
  const { formatMessage } = useIntl()
  const [isCopying, setIsCopying] = useState(false)

  const handleCopyProduct = async () => {
    setIsCopying(true)

    try {
      await copyProduct(productId, formatMessage)

      showToast({
        message: formatMessage({
          id: 'admin/admin.catalog.copy-product.success',
          defaultMessage: 'Product copied successfully',
        }),
        variant: 'positive',
      })
    } catch (_) {
      showToast({
        message: formatMessage({
          id: 'admin/admin.catalog.copy-product.error',
          defaultMessage: 'Error copying product',
        }),
        variant: 'critical',
      })
    }

    setIsCopying(false)
  }

  return {
    isCopying,
    handleCopyProduct,
  }
}
